import React from 'react';
import { AlertCircle, RefreshCw, ChevronLeft } from 'lucide-react';
import { useAnalysisContext } from '../../../../context/AnalysisContext';

/**
 * ErrorState Component - Shows error message when analysis fails
 *
 * Features:
 * - Clear error display
 * - Retry button
 * - Back button to adjust parameters
 * - Retry count display
 */

interface ErrorStateProps {
  error: Error | any;
  onRetry: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => {
  const { goToPreviousStep, retryCount, exitRefinementMode } = useAnalysisContext();

  const getErrorMessage = (): string => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.response?.data?.detail) return error.response.data.detail;
    return 'An unexpected error occurred during analysis';
  };

  const getErrorType = (): string => {
    const message = getErrorMessage().toLowerCase();

    if (message.includes('timeout') || message.includes('timed out')) {
      return 'timeout';
    }
    if (message.includes('no results') || message.includes('no significant')) {
      return 'no_results';
    }
    if (message.includes('network') || message.includes('connection')) {
      return 'network';
    }
    if (message.includes('dataset') || message.includes('not found')) {
      return 'data';
    }
    return 'unknown';
  };

  const getSuggestion = (): string => {
    const errorType = getErrorType();

    switch (errorType) {
      case 'timeout':
        return 'Try reducing the number of cells or genes in your analysis parameters.';
      case 'no_results':
        return 'Try relaxing your analysis parameters (e.g., lower fold-change threshold, increase p-value).';
      case 'network':
        return 'Please check your internet connection and try again.';
      case 'data':
        return 'The selected dataset may not be available. Try selecting a different dataset or disease.';
      default:
        return 'Please try again or adjust your analysis parameters.';
    }
  };

  const handleGoBack = () => {
    exitRefinementMode();
    goToPreviousStep();
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[600px] px-6">
      {/* Error Icon */}
      <div className="w-20 h-20 rounded-full bg-[var(--error)]/10 flex items-center justify-center mb-6">
        <AlertCircle className="w-10 h-10 text-[var(--error)]" />
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
        Analysis Failed
      </h2>

      {/* Error Message */}
      <div className="origin-card p-6 max-w-2xl w-full mb-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">
            Error Details:
          </h3>
          <p className="text-[var(--text-primary)] font-mono text-sm bg-[var(--bg-secondary)] p-3 rounded">
            {getErrorMessage()}
          </p>
        </div>

        {/* Suggestion */}
        <div className="p-4 bg-[var(--origin-blue-500)]/5 border border-[var(--origin-blue-500)]/20 rounded-lg">
          <h4 className="text-sm font-semibold text-[var(--origin-blue-500)] mb-2">
            Suggestion:
          </h4>
          <p className="text-sm text-[var(--text-secondary)]">
            {getSuggestion()}
          </p>
        </div>

        {/* Retry Count */}
        {retryCount > 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-[var(--text-tertiary)]">
              Retry attempts: {retryCount}/3
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleGoBack}
          className="origin-btn origin-btn-ghost flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Adjust Parameters
        </button>

        <button
          onClick={onRetry}
          disabled={retryCount >= 3}
          className="origin-btn origin-btn-primary flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Retry Analysis
        </button>
      </div>

      {/* Max Retries Warning */}
      {retryCount >= 3 && (
        <div className="mt-6 p-4 bg-[var(--warning)]/10 border border-[var(--warning)]/20 rounded-lg max-w-md">
          <p className="text-sm text-[var(--text-secondary)] text-center">
            Maximum retry attempts reached. Please adjust your parameters or try a different analysis configuration.
          </p>
        </div>
      )}

      {/* Common Issues */}
      <div className="mt-8 max-w-2xl">
        <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 text-center">
          Common Issues & Solutions:
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-[var(--bg-secondary)] rounded-lg">
            <h5 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              No Results Found
            </h5>
            <p className="text-xs text-[var(--text-tertiary)]">
              Lower your thresholds or select different cell types
            </p>
          </div>

          <div className="p-4 bg-[var(--bg-secondary)] rounded-lg">
            <h5 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              Timeout Error
            </h5>
            <p className="text-xs text-[var(--text-tertiary)]">
              Reduce max_cells or use a quicker preset
            </p>
          </div>

          <div className="p-4 bg-[var(--bg-secondary)] rounded-lg">
            <h5 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              Dataset Not Found
            </h5>
            <p className="text-xs text-[var(--text-tertiary)]">
              Try selecting a different dataset in Step 1.5
            </p>
          </div>

          <div className="p-4 bg-[var(--bg-secondary)] rounded-lg">
            <h5 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
              Memory Error
            </h5>
            <p className="text-xs text-[var(--text-tertiary)]">
              Reduce the number of genes or cells analyzed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorState;
